import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { submitOrder } from "../services/orderService";
import { getCustomerRecords, getAllProducts } from "../services/dataCache";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { isPromoActive, effectivePrice } from "../utils/promo";
import { getCustomerPricing, specialPriceFor } from "../services/customerPricingService";
import { sortOrderItems } from "../utils/orderSort";
import { productMatches, productMatchesRelaxed, relaxedScore } from "../utils/searchMatch";
import FloatingDictation from "../components/FloatingDictation";
import OrderSheetModal from "../components/OrderSheetModal";

// SKUs sort numerically where possible (matches the catalog's ordering).
function skuCompare(a, b) {
  const numA = parseInt(a.sku, 10);
  const numB = parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

const QUICK_ADD_LIMIT = 8; // keep the results list short and phone-friendly

export default function Cart({ items, setItems, orderFor, setOrderFor, comment, setComment, onAddItem, pasteImported, setPasteImported }) {
  const { user, profile } = useAuth();
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { orderNumber, forName }
  // Tapping the navbar 🛒 while the "order placed" screen is up should return
  // to the cart builder (Brian, 2026-09-01). Same route → no remount, but every
  // navigation gets a fresh location.key, so keying on it clears the screen.
  const location = useLocation();
  useEffect(() => { setConfirmation(null); }, [location.key]);
  const [submitError, setSubmitError] = useState("");
  // note: `comment` state lives in App (persists across navigation)
  // One ID per checkout attempt: generated on first submit, reused on retries,
  // cleared only after success. Guarantees a retry can't create a second order.
  const attemptIdRef = useRef(null);

  // Rep/admin order-on-behalf (backlog #2): staff pick which customer RECORD
  // the order is for. Customers never see this panel and order as themselves.
  const isStaff = profile?.role === "rep" || profile?.role === "admin";
  // Per-customer price visibility (backlog #4) — hides ONLY price columns and
  // the total; ordering works normally (real prices ride the order for QB).
  const showPrices = isStaff || profile?.showPrices !== false;
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(isStaff);
  const [custSearch, setCustSearch] = useState("");

  // Rep item search (backlog #3): staff-only quick-add so a rep on the phone
  // can build an order without leaving the cart. Reads the session product
  // cache — 0 Firestore reads on revisit. Matching is a plain substring on
  // name/SKU/brand, so it works in BOTH Korean and English (the product name
  // is the bilingual QB description string).
  const [products, setProducts] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [toast, setToast] = useState(null);

  // Paste-order import (Brian, 2026-08-25): Claude (or anyone) transcribes a
  // customer PO — photo, email, fax — into a dead-simple text format, and
  // this swallows it into the cart. The "eyes" are human/AI outside the
  // portal; the portal just parses:
  //   store: <name to match>      (optional — picks the Ordering-for store)
  //   note: <free text>           (optional, appends to the order note)
  //   <SKU> x <qty>               (also "10007 5", "10007,5", "10007*5")
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteReport, setPasteReport] = useState(null);
  // pasteImported (props, App state since 2026-09-01): true once this cart was
  // built by a paste import — the order then syncs to QB in AS-WRITTEN line
  // order (orderService keepWrittenOrder). Survives navigation + refresh.

  function clearCart() {
    if (!window.confirm(t("confirmClearCart", items.length))) return;
    setItems([]);
    setPasteImported(false);
    popToast("🗑");
  }

  function handlePasteImport() {
    // Double-paste guard (Brian, 2026-08-26): importing MERGES by SKU, so
    // pasting the same block twice doubles quantities. Warn when the cart
    // isn't empty.
    if (items.length > 0 && !window.confirm(
      `The cart already has ${items.length} line${items.length !== 1 ? "s" : ""}. Importing ADDS to them (same SKUs merge quantities).\n\nContinue? (Cancel, then "Remove all items" first if this is a re-paste.)`)) return;
    const lines = pasteText.split(/\r?\n/);
    const parsed = [];
    const unknown = [];
    let storeQuery = null;
    const notes = [];
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;
      const storeM = line.match(/^store\s*[:=]\s*(.+)$/i);
      if (storeM) { storeQuery = storeM[1].trim(); continue; }
      const noteM = line.match(/^note\s*[:=]\s*(.+)$/i);
      if (noteM) { notes.push(noteM[1].trim()); continue; }
      const m = line.match(/^([A-Za-z0-9-]+)\s*(?:[x×,*]\s*|\s+)(\d+(?:\.\d+)?)$/i);
      if (!m) { unknown.push(line); continue; }
      parsed.push({ sku: m[1], qty: parseFloat(m[2]) });
    }
    // merge duplicate SKU lines, then match against the product list
    const bySku = new Map();
    for (const it of parsed) {
      bySku.set(it.sku, (bySku.get(it.sku) || 0) + it.qty);
    }
    let added = 0;
    const noProduct = [];
    for (const [sku, qty] of bySku) {
      const p = products.find(x => x.sku.toUpperCase() === sku.toUpperCase());
      if (!p || !(qty > 0)) { noProduct.push(`${sku} x ${qty}`); continue; }
      onAddItem(p, qty);
      added++;
    }
    // store line → try to select the Ordering-for record
    let storeMsg = null;
    if (storeQuery) {
      const q = storeQuery.toLowerCase();
      const hits = records.filter(r =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.qbCustomerName || "").toLowerCase().includes(q));
      if (hits.length === 1) {
        setOrderFor({ type: "record", id: hits[0].id, name: hits[0].name, linkedUserId: hits[0].linkedUserId || null });
        storeMsg = `Store: ${hits[0].name}`;
      } else {
        storeMsg = hits.length === 0
          ? `⚠ Store "${storeQuery}" not found — pick it above.`
          : `⚠ Store "${storeQuery}" matches ${hits.length} records — pick it above.`;
      }
    }
    if (notes.length > 0) {
      setComment(c => ((c ? c + " " : "") + notes.join(" ")).slice(0, 500));
    }
    setPasteReport({ added, noProduct, unknown, storeMsg });
    if (added > 0) {
      setPasteText("");
      setPasteImported(true);
    }
  }

  // Order drafts (Brian, 2026-07-14): park an unapproved order at one store,
  // take the next store's order, come back later. Device-local per account.
  const draftsKey = user ? `sunjin-drafts-${user.uid}` : null;
  const [drafts, setDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`sunjin-drafts-${user?.uid}`) || "[]"); }
    catch { return []; }
  });
  function persistDrafts(next) {
    setDrafts(next);
    try {
      localStorage.setItem(draftsKey, JSON.stringify(next,
        (_, v) => v && typeof v === "object" && typeof v.toMillis === "function" ? v.toMillis() : v));
    } catch { /* storage unavailable — drafts just won't persist */ }
  }

  function saveDraft() {
    if (items.length === 0) return;
    const committed = itemsWithCommittedQty();
    const draft = {
      id: `${Date.now()}`,
      savedAt: Date.now(),
      label: orderFor?.type === "record" ? orderFor.name : (orderFor?.type === "self" ? "My own account" : "(no customer picked)"),
      items: committed,
      orderFor,
      comment,
      pasteImported // as-written QB order survives park & resume
    };
    persistDrafts([draft, ...drafts]);
    setItems([]);
    setOrderFor(null);
    setComment("");
    setPasteImported(false);
    popToast(`Draft saved — ${draft.label}`);
  }

  function resumeDraft(draft) {
    if (items.length > 0 && !window.confirm("Replace the current order with this draft? The current items will be discarded.")) return;
    setItems(draft.items || []);
    setOrderFor(draft.orderFor || null);
    setComment(draft.comment || "");
    setPasteImported(draft.pasteImported === true);
    persistDrafts(drafts.filter(d => d.id !== draft.id));
    popToast(`Draft resumed — ${draft.label}`);
  }

  function deleteDraft(draft) {
    if (!window.confirm(`Delete the saved draft for ${draft.label}?`)) return;
    persistDrafts(drafts.filter(d => d.id !== draft.id));
  }

  function popToast(message, duration = 1800) {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  }

  useEffect(() => {
    if (!isStaff) return;
    async function loadRecords() {
      const recs = await getCustomerRecords(); // session cache — 0 reads on revisit
      setRecords([...recs].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setRecordsLoading(false);
    }
    loadRecords();
  }, [isStaff]);

  useEffect(() => {
    if (!isStaff) return;
    async function loadProducts() {
      const all = await getAllProducts(); // session cache — shared with the catalog
      setProducts([...all].sort(skuCompare)); // full list; returns need inactive items too
    }
    loadProducts();
  }, [isStaff]);

  // Cart lines are keyed by cartKey when present (returns/credit lines share
  // a SKU with normal lines), falling back to sku for regular items.
  const lineKey = (i) => i.cartKey ?? i.sku;

  function updateQty(key, qty) {
    setItems(prev => prev.map(i => {
      if (lineKey(i) !== key) return i;
      // Return lines live in negative territory; normal lines in positive.
      const clamped = (i.isReturn || i.qty < 0) ? Math.min(-0.25, qty) : Math.max(0.25, qty);
      return { ...i, qty: clamped };
    }));
  }

  function removeItem(key) {
    setItems(items.filter(i => lineKey(i) !== key));
  }

  // Freely-editable quantity fields (Brian, 2026-07-14): the old inline
  // parse clamped to 0.25 on every keystroke, so backspacing hard-locked the
  // box. Edits are drafted as raw text and committed on blur / at submit.
  const [qtyEdits, setQtyEdits] = useState({});

  function commitQty(key) {
    const raw = qtyEdits[key];
    if (raw === undefined) return;
    const n = parseFloat(raw);
    if (!isNaN(n) && n !== 0) updateQty(key, n);
    setQtyEdits(prev => { const next = { ...prev }; delete next[key]; return next; });
  }

  // Store-specific deal pricing (Brian, 2026-07-14, staff only): each line's
  // unit price is editable (priceOverride, exempt from live repricing), and
  // freeQty splits off a second $0 line of the same product at submission —
  // the 1+1/2+2 pattern QB expects (full-price line + zero-price line).
  const [priceEdits, setPriceEdits] = useState({});

  function commitPrice(key) {
    const raw = priceEdits[key];
    if (raw === undefined) return;
    const n = parseFloat(raw);
    if (!isNaN(n) && n >= 0) {
      setItems(prev => prev.map(i => lineKey(i) === key ? { ...i, priceOverride: n } : i));
    }
    setPriceEdits(prev => { const next = { ...prev }; delete next[key]; return next; });
  }

  function clearPriceOverride(key) {
    setItems(prev => prev.map(i => {
      if (lineKey(i) !== key) return i;
      const { priceOverride, ...rest } = i;
      return rest;
    }));
    setPriceEdits(prev => { const next = { ...prev }; delete next[key]; return next; });
  }

  function setFreeQty(key, n) {
    setItems(prev => prev.map(i => lineKey(i) === key ? { ...i, freeQty: Math.max(0, Math.floor(n) || 0) } : i));
  }

  // Store-specific special pricing (2026-08-24): load the pricing list for
  // whoever this order is FOR — the selected store (staff) or the logged-in
  // customer's own store. Display only; the order transaction re-reads the
  // doc live at submit and is the authority.
  const pricingRecordId = isStaff
    ? (orderFor?.type === "record" ? orderFor.id : null)
    : (profile?.customerRecordId || null);
  const [storePricing, setStorePricing] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!pricingRecordId) { setStorePricing(null); return; }
    getCustomerPricing(pricingRecordId)
      .then(d => { if (alive) setStorePricing(d); })
      .catch(() => { if (alive) setStorePricing(null); });
    return () => { alive = false; };
  }, [pricingRecordId]);

  const specialFor = (sku) => specialPriceFor(storePricing, sku);

  // Unit price for display/totals: credit keeps its own, override wins,
  // then the store's special price, else live promo/base.
  const unitPrice = (i) => i.creditLine ? i.priceAtOrder
    : (i.priceOverride ?? specialFor(i.sku) ?? effectivePrice(i));

  // Floating dictation (staff, 2026-08-24): ONE mic follows the scroll and
  // types into whichever NOTE box was last tapped (order note / line note /
  // credit text). Note fields mark themselves on focus; default is the
  // order-note box. Appends with a space, clipped to the field's max length.
  const voiceTargetRef = useRef(null); // { apply(text) }

  function dictateToComment(tx) {
    setComment(c => ((c ? c + " " : "") + tx).slice(0, 500));
  }

  function handleDictation(text) {
    const tgt = voiceTargetRef.current;
    if (tgt) tgt.apply(text);
    else if (isStaff) setItemSearch(text); // nothing tapped: staff = find a product
    else dictateToComment(text);           // customers: the order-note box
    popToast(`🎤 ${text}`);
  }

  // Submission-safe view of items with any in-progress qty edits applied.
  function itemsWithCommittedQty() {
    return items.map(i => {
      const raw = qtyEdits[lineKey(i)];
      if (raw === undefined) return i;
      const n = parseFloat(raw);
      if (isNaN(n) || n === 0) return i;
      const clamped = (i.isReturn || i.qty < 0) ? Math.min(-0.25, n) : Math.max(0.25, n);
      return { ...i, qty: clamped };
    });
  }

  // Staff-only per-line note (e.g. store-specific pricing intel). Rides the
  // order line to admin Orders + the packing sheet; NEVER shown to the
  // customer and NEVER sent to QuickBooks.
  function updateLineNote(key, text) {
    setItems(prev => prev.map(i => lineKey(i) === key ? { ...i, lineNote: text } : i));
  }

  // Promo-aware display prices (overrides win). The order transaction
  // recomputes non-overridden prices from live data at submit time.
  const total = items.reduce((sum, i) => sum + i.qty * unitPrice(i), 0);

  // On-screen order sheet (Brian, 2026-08-20): one click → popup showing the
  // order exactly like the packing-list printout — SKU/name/qty in canonical
  // storage order, NO prices/discounts/notes/comments — so quantities can be
  // reviewed and adjusted on the fly without printing to PDF. All roles.
  const [sheetOpen, setSheetOpen] = useState(false);

  // Returns BOTH orderings: packing sort (warehouse view) and the original
  // entry order — the latter matches a handwritten PO line-for-line when the
  // cart came from a paste import (Brian, 2026-08-26).
  function orderSheetRows() {
    const expanded = [];
    for (const i of itemsWithCommittedQty()) {
      expanded.push(i);
      // Free goods appear as their own row, same as on the real packing sheet
      // (they're physically packed cases even though they ride at $0).
      const free = isStaff && !i.isReturn && !i.creditLine ? Math.floor(i.freeQty || 0) : 0;
      if (free > 0) expanded.push({ ...i, qty: free, freeGoods: true, cartKey: `${lineKey(i)}:free` });
    }
    // Cart items are full product spreads, so each carries its storageMethod.
    const storageBySku = {};
    for (const i of expanded) {
      if (i.storageMethod) storageBySku[String(i.sku)] = i.storageMethod;
    }
    return { original: expanded, sorted: sortOrderItems(expanded, storageBySku).sorted };
  }

  const filteredRecords = records.filter(r => {
    const s = custSearch.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(s) ||
      (r.qbCustomerName || "").toLowerCase().includes(s) ||
      (r.phone || "").includes(custSearch)
    );
  });

  // Quick-add matches only once the rep has typed something; capped so the
  // list stays short. Uses the same add path as the catalog (merge-by-SKU).
  const itemQuery = itemSearch.trim().toLowerCase();
  let itemMatches = itemQuery
    ? products.filter(p => productMatches(p, itemSearch)).slice(0, QUICK_ADD_LIMIT)
    : [];
  // Exact search empty → relaxed keyword retry (간장게장 → 간장 + 게장),
  // best-covered matches first so the intended product tops the short list.
  if (itemQuery && itemMatches.length === 0) {
    itemMatches = products
      .filter(p => productMatchesRelaxed(p, itemSearch))
      .sort((a, b) => relaxedScore(b, itemSearch) - relaxedScore(a, itemSearch) || skuCompare(a, b))
      .slice(0, QUICK_ADD_LIMIT);
  }

  function handleQuickAdd(p) {
    onAddItem(p, 1); // rep tunes the exact qty in the order table below
    popToast(t("addedName", p.name));
  }

  // ---- Returns & credit (Brian, 2026-07-14, staff only) ----
  // Returns: negative-quantity lines on the SAME invoice (10001 × -1) —
  // stock self-adjusts (a return puts cases back) and QB accepts the
  // negative line natively. Searches ALL products incl. inactive ones
  // (returned items are often discontinued).
  const [returnSearch, setReturnSearch] = useState("");
  const returnQuery = returnSearch.trim().toLowerCase();
  let returnMatches = returnQuery
    ? products.filter(p => productMatches(p, returnSearch)).slice(0, QUICK_ADD_LIMIT)
    : [];
  if (returnQuery && returnMatches.length === 0) {
    returnMatches = products
      .filter(p => productMatchesRelaxed(p, returnSearch))
      .sort((a, b) => relaxedScore(b, returnSearch) - relaxedScore(a, returnSearch) || skuCompare(a, b))
      .slice(0, QUICK_ADD_LIMIT);
  }

  function handleAddReturn(p) {
    const key = `${p.sku}:ret`;
    setItems(prev => {
      const existing = prev.find(i => lineKey(i) === key);
      if (existing) {
        return prev.map(i => lineKey(i) === key ? { ...i, qty: i.qty - 1 } : i);
      }
      return [...prev, { ...p, qty: -1, isReturn: true, cartKey: key }];
    });
    setReturnSearch("");
    popToast(`↩ Return added: ${p.name}`);
  }

  // Credit: a free-text line that becomes QB item 10000 with the text as the
  // line description and a negative amount. Item 10000 must exist in QB.
  const [creditText, setCreditText] = useState("");
  const [creditAmount, setCreditAmount] = useState("");

  function handleAddCredit() {
    const amt = parseFloat(creditAmount);
    const text = creditText.trim();
    if (!text || isNaN(amt) || amt <= 0) return;
    setItems(prev => ([...prev, {
      sku: "10000",
      name: "Credit",
      creditLine: true,
      creditDesc: text.slice(0, 200),
      qty: 1,
      priceAtOrder: -amt,
      basePrice: -amt,
      cartKey: `credit-${Date.now()}`
    }]));
    setCreditText("");
    setCreditAmount("");
    popToast(`Credit added: -$${amt.toFixed(2)}`);
  }

  async function handleSubmit() {
    if (submitting) return; // extra guard against rapid double-click
    setSubmitError("");

    if (items.length === 0) {
      setSubmitError(t("addItemFirst"));
      return;
    }

    // Staff must say who the order is for. Admin may pick "my own account"
    // (test orders); reps must always pick a customer record.
    const forSelf = !isStaff || orderFor?.type === "self";
    if (isStaff && !orderFor) {
      setSubmitError(t("selectCustomerFirst"));
      return;
    }

    // QuickBooks generally refuses invoices with a NEGATIVE grand total —
    // warn before submitting one (pure-returns situations belong in a QB
    // credit memo instead).
    const previewItems = itemsWithCommittedQty();
    const previewTotal = previewItems.reduce((s, i) => s + i.qty * unitPrice(i), 0);
    if (previewTotal < 0 && !window.confirm(
      `This order's total is NEGATIVE ($${previewTotal.toFixed(2)}). QuickBooks may reject the invoice — a pure return/credit is usually better handled as a QB credit memo.\n\nSubmit anyway?`)) {
      return;
    }

    setSubmitting(true);
    if (!attemptIdRef.current) {
      attemptIdRef.current = crypto.randomUUID();
    }

    try {
      const finalItems = previewItems;
      const orderItems = [];
      for (const i of finalItems) {
        const overridden = isStaff && !i.creditLine && i.priceOverride != null;
        orderItems.push({
          sku: i.sku,
          qty: i.qty,
          priceAtOrder: i.creditLine ? i.priceAtOrder
            : overridden ? i.priceOverride
            : (specialFor(i.sku) ?? effectivePrice(i)),
          ...(overridden || i.creditLine ? { customPrice: true } : {}),
          ...(isStaff && i.lineNote?.trim() ? { lineNote: i.lineNote.trim().slice(0, 200) } : {}),
          ...(i.isReturn ? { isReturn: true } : {}),
          ...(i.creditLine ? { creditLine: true, creditDesc: (i.creditDesc || "").slice(0, 200) } : {})
        });
        // 1+1 / 2+2: the free units become a second $0 line of the same product
        const free = isStaff && !i.isReturn && !i.creditLine ? Math.floor(i.freeQty || 0) : 0;
        if (free > 0) {
          orderItems.push({ sku: i.sku, qty: free, priceAtOrder: 0, customPrice: true, freeGoods: true });
        }
      }
      // Store-based linking (2026-08-20): accounts point at customer records
      // via users.customerRecordId (several accounts may share one store).
      // Staff order-for-record: the store's oldest-registered account becomes
      // customerId (keeps confirmation emails working exactly as before);
      // every account of the store sees the order via the record id.
      // Self-order: stamp the customer's own store too, so the invoice syncs
      // under the record's exact QB name and co-managers see the order.
      let customerId = null;
      let customerRecordId = null;
      if (forSelf) {
        customerId = user.uid;
        customerRecordId = profile?.customerRecordId || null;
      } else {
        customerRecordId = orderFor.id;
        try {
          const accSnap = await getDocs(query(
            collection(db, "users"),
            where("role", "==", "customer"),
            where("customerRecordId", "==", orderFor.id)
          ));
          const linked = accSnap.docs
            .map(d => ({ uid: d.id, ...d.data() }))
            .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
          // Legacy fallback: records linked before the store-based migration
          // still carry linkedUserId — honor it until migration runs.
          customerId = linked[0]?.uid || orderFor.linkedUserId || null;
        } catch {
          customerId = orderFor.linkedUserId || null;
        }
      }
      const result = await submitOrder({
        orderId: attemptIdRef.current,
        customerId,
        placedBy: user.uid,
        customerRecordId,
        items: orderItems,
        comment,
        keepWrittenOrder: isStaff && pasteImported
      });
      setConfirmation({
        orderNumber: result.orderNumber,
        forName: forSelf ? null : orderFor.name
      });
      setItems([]);
      setOrderFor(null);
      setComment("");
      setPasteImported(false);
      attemptIdRef.current = null; // only reset after confirmed success
    } catch (err) {
      alert(t("orderFailed", err.message));
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmation) {
    return (
      <div className="cart-confirmation">
        <h2>{t("orderPlaced")}</h2>
        <p>{t("orderNumber")} <strong>{confirmation.orderNumber}</strong></p>
        {confirmation.forName && (
          <p>{t("placedFor")} <strong>{confirmation.forName}</strong></p>
        )}
        <p>{t("invoiceFollowUp")}</p>
      </div>
    );
  }

  const empty = items.length === 0;
  // Customers with an empty cart just see the empty message. Staff always get
  // the full builder (customer picker + quick-add) so they can start an order
  // from scratch here without bouncing to the catalog first.
  if (empty && !isStaff) return <p>{t("cartEmpty")}</p>;

  return (
    <div className="cart">
      <h2>{t("yourOrder")}</h2>

      {isStaff && (
        <div className="order-for-panel">
          <p className="order-for-label">{t("orderingFor")}</p>
          {orderFor ? (
            <div className="order-for-selected">
              <strong>
                {orderFor.type === "self" ? t("myOwnAccount") : orderFor.name}
              </strong>
              <button className="mini-btn" onClick={() => setOrderFor(null)}>{t("change")}</button>
            </div>
          ) : recordsLoading ? (
            <p className="order-for-hint">{t("loadingCustomers")}</p>
          ) : (
            <>
              <input
                className="order-for-search"
                placeholder={t("searchCustomers")}
                value={custSearch}
                onFocus={() => { voiceTargetRef.current = { apply: (tx) => setCustSearch(tx) }; }}
                onChange={(e) => setCustSearch(e.target.value)}
              />
              {/* Scrollable dropdown of all matches — search above narrows it */}
              <select
                className="order-for-dropdown"
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  if (v === "__self__") {
                    setOrderFor({ type: "self" });
                    return;
                  }
                  const r = records.find(x => x.id === v);
                  if (r) {
                    setOrderFor({
                      type: "record",
                      id: r.id,
                      name: r.name,
                      linkedUserId: r.linkedUserId || null
                    });
                  }
                }}
              >
                <option value="">
                  {t("selectCustomer", filteredRecords.length)}
                </option>
                {profile.role === "admin" && (
                  <option value="__self__">{t("myOwnAccount")}</option>
                )}
                {filteredRecords.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.phone ? ` · ${r.phone}` : ""}
                  </option>
                ))}
              </select>
              {records.length > 0 && filteredRecords.length === 0 && (
                <p className="order-for-hint">{t("noCustomersMatch")}</p>
              )}
              {records.length === 0 && (
                <p className="order-for-hint">{t("noCustomerRecords")}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Paste-order import (staff): text block from Claude/email -> cart */}
      {isStaff && (
        <div className="item-search-panel">
          <p className="order-for-label">
            📥 Paste order
            <button className="mini-btn" style={{ marginLeft: 10 }} onClick={() => { setPasteOpen(o => !o); setPasteReport(null); }}>
              {pasteOpen ? "Close" : "Open"}
            </button>
          </p>
          {pasteOpen && (
            <>
              <textarea
                className="order-comment-input"
                rows={6}
                placeholder={"store: A-Mart\nnote: extra instructions...\n10007 x 5\n30053 x 1\nW0003 x 72"}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <button className="mini-btn" disabled={!pasteText.trim()} onClick={handlePasteImport}>
                Import into cart
              </button>
              {pasteReport && (
                <div className="order-for-hint">
                  <p>✔ Added {pasteReport.added} item{pasteReport.added !== 1 ? "s" : ""} to the cart.{pasteReport.storeMsg ? ` ${pasteReport.storeMsg}` : ""}</p>
                  {pasteReport.noProduct.length > 0 && (
                    <p className="error">Unknown SKUs (not added): {pasteReport.noProduct.join(", ")}</p>
                  )}
                  {pasteReport.unknown.length > 0 && (
                    <p className="error">Lines I couldn't read: {pasteReport.unknown.join(" | ")}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Rep item search (backlog #3) — quick-add straight into the order */}
      {isStaff && (
        <div className="item-search-panel">
          <p className="order-for-label">{t("addItemsLabel")}</p>
          <input
            className="item-search-input"
            placeholder={t("addItemsPlaceholder")}
            value={itemSearch}
            onFocus={() => { voiceTargetRef.current = { apply: (tx) => setItemSearch(tx) }; }}
            onChange={(e) => setItemSearch(e.target.value)}
          />
          {itemQuery && itemMatches.length === 0 && (
            <p className="order-for-hint">{t("noProductsMatch")}</p>
          )}
          {itemMatches.length > 0 && (
            <ul className="item-search-results">
              {itemMatches.map(p => {
                const wholeStock = Math.floor(p.stockQty);
                const displayStock = Math.ceil(p.stockQty * 100) / 100;
                const outOfStock = wholeStock <= 0;
                const unit = effectivePrice(p);
                return (
                  <li key={p.sku} className="item-search-row">
                    <div className="item-search-info">
                      <span className="item-search-name">{p.name}</span>
                      <span className="item-search-meta">
                        {t("sku")} {p.sku}
                        {p.brand ? ` · ${p.brand}` : ""}
                        {showPrices && (
                          isPromoActive(p)
                            ? ` · $${unit.toFixed(2)} ${t("promoTag")}`
                            : ` · $${unit.toFixed(2)}`
                        )}
                        {" · "}
                        {outOfStock ? t("outOfStock") : t("inStock", displayStock)}
                      </span>
                    </div>
                    <button
                      className="mini-btn"
                      title={outOfStock ? t("oosNote") : undefined}
                      onClick={() => handleQuickAdd(p)}
                    >
                      {t("add")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {isStaff && (
        <div className="returns-credit-panel">
          <p className="order-for-label">↩ Returns &amp; credit (staff only — lines go on this same invoice)</p>
          <div className="returns-credit-grid">
            <div>
              <input
                className="item-search-input"
                placeholder="Return an item — search name, SKU, or brand..."
                value={returnSearch}
                onFocus={() => { voiceTargetRef.current = { apply: (tx) => setReturnSearch(tx) }; }}
                onChange={(e) => setReturnSearch(e.target.value)}
              />
              {returnQuery && returnMatches.length === 0 && (
                <p className="order-for-hint">{t("noProductsMatch")}</p>
              )}
              {returnMatches.length > 0 && (
                <ul className="item-search-results">
                  {returnMatches.map(p => (
                    <li key={p.sku} className="item-search-row">
                      <div className="item-search-info">
                        <span className="item-search-name">{p.name}</span>
                        <span className="item-search-meta">
                          {t("sku")} {p.sku}{!p.active ? " · (inactive)" : ""}
                        </span>
                      </div>
                      <button className="mini-btn" onClick={() => handleAddReturn(p)}>↩ Return</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <input
                className="item-search-input"
                placeholder="Credit note — e.g. damaged case last delivery"
                maxLength={200}
                value={creditText}
                onFocus={() => {
                  voiceTargetRef.current = {
                    apply: (tx) => setCreditText(c => ((c ? c + " " : "") + tx).slice(0, 200))
                  };
                }}
                onChange={(e) => setCreditText(e.target.value)}
              />
              <div className="credit-amount-row">
                <span>Credit $</span>
                <input
                  type="number"
                  className="credit-amount-input"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                />
                <button
                  className="mini-btn"
                  disabled={!creditText.trim() || !(parseFloat(creditAmount) > 0)}
                  onClick={handleAddCredit}
                >Add credit</button>
              </div>
              <p className="order-for-hint">Goes on the invoice as item 10000 with your text as its description.</p>
            </div>
          </div>
        </div>
      )}

      {isStaff && drafts.length > 0 && (
        <div className="drafts-panel">
          <p className="order-for-label">💾 Saved orders ({drafts.length})</p>
          {drafts.map(d => (
            <div key={d.id} className="draft-row">
              <span className="draft-label">
                {d.label} · {(d.items || []).length} item{(d.items || []).length !== 1 ? "s" : ""} ·{" "}
                {new Date(d.savedAt).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}
              </span>
              <button className="mini-btn" onClick={() => resumeDraft(d)}>Resume</button>
              <button className="mini-btn danger" onClick={() => deleteDraft(d)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {empty ? (
        <p className="order-for-hint">{t("emptyOrderHint")}</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>{t("product")}</th><th>{t("qty")}</th>
                {showPrices && <><th>{t("price")}</th><th>{t("subtotal")}</th></>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => {
                const unit = unitPrice(i);
                const key = lineKey(i);
                return (
                  <tr key={key} className={i.isReturn || i.creditLine ? "return-line" : ""}>
                    <td>
                      {i.isReturn && <span className="return-tag">↩ RETURN </span>}
                      {i.creditLine ? <>Credit — {i.creditDesc}</> : i.name}
                      {isStaff && !i.creditLine && !i.isReturn && (
                        <span className="free-qty-wrap">
                          🎁 Free units:{" "}
                          <input
                            type="number"
                            className="free-qty-input"
                            min={0}
                            step={1}
                            value={i.freeQty || 0}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setFreeQty(key, parseInt(e.target.value, 10))}
                          />
                          {(i.freeQty || 0) > 0 && <span className="free-tag"> → +{i.freeQty} at $0</span>}
                        </span>
                      )}
                      {isStaff && !i.creditLine && (
                        <input
                          className="line-note-input"
                          maxLength={200}
                          placeholder={t("lineNotePlaceholder")}
                          value={i.lineNote || ""}
                          onFocus={() => {
                            voiceTargetRef.current = {
                              apply: (tx) => setItems(prev => prev.map(x =>
                                lineKey(x) === key
                                  ? { ...x, lineNote: (((x.lineNote || "") + " " + tx).trim()).slice(0, 200) }
                                  : x))
                            };
                          }}
                          onChange={(e) => updateLineNote(key, e.target.value)}
                        />
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        step={0.25}
                        value={qtyEdits[key] ?? i.qty}
                        disabled={!!i.creditLine}
                        onChange={(e) => setQtyEdits(prev => ({ ...prev, [key]: e.target.value }))}
                        onBlur={() => commitQty(key)}
                      />
                    </td>
                    {showPrices && (
                      <>
                        <td>
                          {isStaff && !i.creditLine ? (
                            <span className="price-override-wrap">
                              $<input
                                type="number"
                                className="price-override-input"
                                min={0}
                                step={0.01}
                                value={priceEdits[key] ?? unit}
                                onChange={(e) => setPriceEdits(prev => ({ ...prev, [key]: e.target.value }))}
                                onBlur={() => commitPrice(key)}
                              />
                              {i.priceOverride != null && (
                                <button
                                  className="mini-btn"
                                  title={`Back to regular price ($${effectivePrice(i).toFixed(2)})`}
                                  onClick={() => clearPriceOverride(key)}
                                >↺</button>
                              )}
                            </span>
                          ) : specialFor(i.sku) != null || isPromoActive(i) ? (
                            <>
                              <s className="price-struck">${i.basePrice.toFixed(2)}</s>{" "}
                              <span className="promo-price">${unit.toFixed(2)}</span>
                            </>
                          ) : (
                            <>${unit.toFixed(2)}</>
                          )}
                        </td>
                        <td>${(i.qty * unit).toFixed(2)}</td>
                      </>
                    )}
                    <td><button onClick={() => removeItem(key)}>{t("remove")}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showPrices && <p className="cart-total">{t("total", total.toFixed(2))}</p>}
        </>
      )}

      <label className="order-comment-label">
        {t("noteLabel")}
        <textarea
          className="order-comment-input"
          placeholder={t("notePlaceholder")}
          rows={3}
          maxLength={500}
          value={comment}
          onFocus={() => { voiceTargetRef.current = { apply: dictateToComment }; }}
          onChange={(e) => setComment(e.target.value)}
        />
      </label>
      {submitError && <p className="error">{submitError}</p>}
      {isStaff && pasteImported && !empty && (
        <p className="aswritten-tag">📥 Paste-imported — QB invoice will keep the as-written line order</p>
      )}
      <button onClick={handleSubmit} disabled={submitting || empty}>
        {submitting ? t("submitting") : t("placeOrder")}
      </button>
      {!empty && (
        <button className="save-draft-btn" onClick={() => setSheetOpen(true)} disabled={submitting}>
          {t("viewOrderSheet")}
        </button>
      )}
      {!empty && (
        <button className="save-draft-btn clear-cart-btn" onClick={clearCart} disabled={submitting}>
          {t("clearCart")}
        </button>
      )}
      {isStaff && !empty && (
        <>
          <button className="save-draft-btn" onClick={saveDraft} disabled={submitting}>
            💾 Save draft &amp; start new order
          </button>
          <button className="save-draft-btn" onClick={() => window.print()} disabled={submitting}>
            🖨 Print order summary
          </button>
        </>
      )}

      {/* Printable one-pager (Brian, 2026-07-14): shown ONLY when printing —
          the rep hands/shows this to the buyer on the spot, before the order
          is placed or synced. */}
      {isStaff && !empty && (
        <div className="order-summary-sheet">
          <div className="packing-header">
            <h1>Order Summary</h1>
            {orderFor && orderFor.type === "record" && (
              <p className="packing-customer"><strong>{orderFor.name}</strong></p>
            )}
            <p className="packing-meta">
              Sunjin Trading Inc. · {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          {comment && (
            <div className="packing-order-note">💬 <strong>NOTE:</strong> {comment}</div>
          )}
          <table className="packing-table">
            <thead>
              <tr><th>SKU</th><th>Description</th><th className="qty-col">Qty</th><th className="qty-col">Price</th><th className="qty-col">Amount</th></tr>
            </thead>
            <tbody>
              {itemsWithCommittedQty().map(i => {
                const unit = unitPrice(i);
                return (
                  <tr key={lineKey(i)}>
                    <td>{i.sku}</td>
                    <td>
                      {i.isReturn && <span className="return-tag">↩ RETURN </span>}
                      {i.creditLine ? <>Credit — {i.creditDesc}</> : i.name}
                      {(i.freeQty || 0) > 0 && (
                        <div className="line-note">🎁 +{i.freeQty} free (at $0)</div>
                      )}
                    </td>
                    <td className="qty-col">{i.qty}</td>
                    <td className="qty-col">${unit.toFixed(2)}</td>
                    <td className="qty-col">${(i.qty * unit).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="cart-total">Total: ${total.toFixed(2)} CAD</p>
        </div>
      )}
      {/* Order-sheet popup (shared with My Orders) — packing-list view of the
          CURRENT cart: SKU/name/qty, list ↔ photos, no prices or notes. */}
      {sheetOpen && (() => {
        const toRow = (i) => ({
          key: lineKey(i) + (i.freeGoods ? ":f" : ""),
          sku: i.sku,
          name: i.name,
          qty: i.qty,
          imageUrl: i.imageUrl || null,
          isReturn: i.isReturn,
          creditLine: i.creditLine,
          creditDesc: i.creditDesc,
          freeGoods: i.freeGoods
        });
        const { original, sorted } = orderSheetRows();
        return (
          <OrderSheetModal
            title={t("yourOrder")}
            customerName={isStaff
              ? (orderFor?.type === "record" ? orderFor.name : null)
              : (profile?.companyName || null)}
            dateLabel={new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
            rows={sorted.map(toRow)}
            originalRows={original.map(toRow)}
            onClose={() => setSheetOpen(false)}
          />
        );
      })()}
      <FloatingDictation onText={handleDictation} />
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
